<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  initialValue: { type: [Number, String, null], default: null },
  anchor: { type: Object, default: null },
});

const emit = defineEmits(['confirm', 'cancel']);

const { t } = useI18n();

const CURRENCIES = [
  { symbol: 'R$', code: 'BRL' },
  { symbol: 'US$', code: 'USD' },
  { symbol: '€', code: 'EUR' },
  { symbol: '£', code: 'GBP' },
];

const dropdownRef = ref(null);
const inputRef = ref(null);
const style = ref({});
const selectedCurrency = ref(CURRENCIES[0]);
const showCurrencyMenu = ref(false);

function formatInitial(val) {
  if (val == null || val === '') return '';
  const num = parseFloat(val);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

const initialDisplay = formatInitial(props.initialValue);

function parseDisplay(str) {
  const cleaned = (str ?? '').trim().replace(/\./g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

function confirm() {
  const text = inputRef.value?.innerText ?? '';
  emit('confirm', parseDisplay(text));
}

function selectCurrency(c) {
  selectedCurrency.value = c;
  showCurrencyMenu.value = false;
  nextTick(() => inputRef.value?.focus());
}

const MIN_WIDTH = 220;

function computeStyle() {
  if (!props.anchor) {
    style.value = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
    };
    return;
  }
  const a = props.anchor;
  const width = Math.max(a.width, MIN_WIDTH);
  const dropH = dropdownRef.value?.offsetHeight ?? 50;
  const spaceBelow = window.innerHeight - a.bottom;
  const spaceAbove = a.top;
  const showAbove = spaceBelow < dropH + 8 && spaceAbove > spaceBelow;

  let left = a.left;
  if (left + width > window.innerWidth - 8)
    left = window.innerWidth - width - 8;
  left = Math.max(8, left);

  if (showAbove) {
    style.value = {
      bottom: `${window.innerHeight - a.top + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    };
  } else {
    style.value = {
      top: `${a.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    };
  }
}

function onDocMousedown(e) {
  if (!dropdownRef.value?.contains(e.target)) {
    confirm();
  }
}

function onKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    if (showCurrencyMenu.value) {
      showCurrencyMenu.value = false;
    } else {
      confirm();
    }
  } else if (e.key === 'Escape') {
    if (showCurrencyMenu.value) {
      showCurrencyMenu.value = false;
    } else {
      emit('cancel');
    }
  }
}

onMounted(() => {
  nextTick(() => {
    computeStyle();
    if (inputRef.value) {
      inputRef.value.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(inputRef.value);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  });
  window.addEventListener('keydown', onKeydown);
  window.addEventListener('mousedown', onDocMousedown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('mousedown', onDocMousedown);
});
</script>

<template>
  <Teleport to="body">
    <div
      ref="dropdownRef"
      class="fixed z-[10010] overflow-visible"
      :style="style"
      @mousedown.stop
    >
      <div
        class="flex items-center gap-1 px-2 py-1.5 bg-n-background border border-n-weak rounded-xl shadow-xl"
      >
        <!-- Seletor de moeda -->
        <div class="relative flex-shrink-0">
          <button
            class="flex items-center gap-0.5 px-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-1 transition-colors focus:outline-none rounded-lg"
            @click.stop="showCurrencyMenu = !showCurrencyMenu"
          >
            {{ selectedCurrency.symbol }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="opacity-60"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div
            v-if="showCurrencyMenu"
            class="absolute top-full left-0 mt-1 z-10 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[80px]"
          >
            <button
              v-for="c in CURRENCIES"
              :key="c.code"
              class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors focus:outline-none"
              :class="{
                'text-n-teal-10 font-medium': c.code === selectedCurrency.code,
              }"
              @click.stop="selectCurrency(c)"
            >
              {{ c.symbol }}
              <span class="text-n-slate-10 text-xs ml-1">{{ c.code }}</span>
            </button>
          </div>
        </div>

        <!-- Divisor -->
        <span class="w-px h-4 bg-n-weak flex-shrink-0" />

        <!-- Campo de valor -->
        <div
          ref="inputRef"
          contenteditable="true"
          inputmode="decimal"
          :data-placeholder="t('KANBAN.WON_VALUE_MODAL.PLACEHOLDER')"
          class="flex-1 min-w-0 pl-2 text-sm text-n-slate-12 outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-n-slate-10"
          @keypress="
            e => {
              if (!/[\d.,]/.test(e.key)) e.preventDefault();
            }
          "
        >
          {{ initialDisplay }}
        </div>

        <!-- Botão confirmar -->
        <button
          class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-n-teal-9 hover:bg-n-teal-10 transition-colors"
          @click.stop="confirm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
    </div>
  </Teleport>
</template>
